import {
  Stack,
  Typography,
  Button,
  Tab,
  Tabs,
  Divider,
  Box,
} from "@mui/material";
import title from "global/constants/TitleConstants";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useTitle } from "utils/UseTitle";
import CreateCampaign from "./CreateCampaign/CreateCampaign";
import { tabs } from "./CampaignConstants";
import EmailTab from "./Components/EmailTab";
import SocialTab from "./Components/SocialTab";
import CreateCampaignBox from "./Components/CreateCampaignBox";
import { useLocation } from "react-router-dom";
import { hasAccessTo } from "layouts/Config";
import strings from "global/constants/StringConstants";
import { useAppSelector } from "utils/hooks";
import { isTrial } from "redux/authSlice";

const Campaigns = () => {
  useTitle(title.CAMPAIGNS);
  const [isLoading, setIsLoading] = useState(false);
  const isTrialAccount = useAppSelector(isTrial);
  const getTab = new URLSearchParams(useLocation().search).get("tab");
  const hasEmailCampaignFetchPermission = hasAccessTo(
    strings.campaign,
    strings.fetchPermission
  );

  const hasSocialCampaignFetchPermission = hasAccessTo(
    strings.social,
    strings.fetchPermission
  );

  const fetchTab = (
    selectedTab: string = "",
    searchBasedOn: "label" | "key"
  ) => {
    const newSelection = tabs().find(
      (tab) => tab[searchBasedOn] === selectedTab
    );
    return newSelection;
  };
  const [createCampaignModalVisibility, setCreateCampaignModalVisibility] =
    useState<boolean>(false);

  useEffect(() => {
    const tabToSelect = fetchTab(getTab ?? "", "key");
    setCurrentTab(tabToSelect?.key ?? tabs()[0].key);
  }, [getTab]);

  const tabToSelect = fetchTab(getTab ?? "", "key");

  const [currentTab, setCurrentTab] = useState(
    tabToSelect?.key ?? tabs()[0].key
  );

  const handleTabsChange = useCallback(
    (event: ChangeEvent<any>, value: string): void => {
      setCurrentTab(value);
    },
    []
  );
  const getCreateCampaignModal = () => {
    return (
      <CreateCampaign
        isDialogOpen={createCampaignModalVisibility}
        handleDialogClose={() => setCreateCampaignModalVisibility(false)}
      />
    );
  };

  const getCampaignsHeader = () => {
    return (
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h5" color="text.primary">
          Campaigns
        </Typography>
        <Button
          variant="contained"
          onClick={() => setCreateCampaignModalVisibility(true)}
        >
          Create Campaign
        </Button>
      </Stack>
    );
  };

  const getTabs = () => {
    const filteredTabs = tabs().filter((tab) => tab.hasPermission);
    const campaignTabs = filteredTabs.filter(
      (tab) => !(isTrialAccount && !tab.permittedForTrial)
    );
    return (
      <Tabs
        indicatorColor="primary"
        onChange={handleTabsChange}
        scrollButtons="auto"
        textColor="primary"
        value={currentTab}
        variant="scrollable"
      >
        {campaignTabs.map((tab) => (
          <Tab
            key={tab.value}
            label={tab.label}
            value={tab.value}
            disabled={isLoading}
          />
        ))}
      </Tabs>
    );
  };

  const getCampaignListBody = () => {
    switch (currentTab) {
      case "email":
        return (
          hasEmailCampaignFetchPermission && (
            <EmailTab
              open={createCampaignModalVisibility}
              setOpen={setCreateCampaignModalVisibility}
            />
          )
        );
      case "social": {
        return (
          hasSocialCampaignFetchPermission && (
            <SocialTab
              open={createCampaignModalVisibility}
              setOpen={setCreateCampaignModalVisibility}
            />
          )
        );
      }
      default: {
        return (
          <CreateCampaignBox
            isOpen={createCampaignModalVisibility}
            setOpen={setCreateCampaignModalVisibility}
          />
        );
      }
    }
  };

  return (
    <>
      <CenteredAuthenticatedLayout>
        {getCampaignsHeader()}
        {getTabs()}
        <Divider flexItem sx={{ mb: 2, backgroundColor: "#47556940" }} />
        {getCampaignListBody()}
      </CenteredAuthenticatedLayout>
      {getCreateCampaignModal()}
    </>
  );
};

export default Campaigns;
